import {
	endOfWeek,
	formatISO,
	getDate,
	getMonth,
	getYear,
	isBefore,
	isWeekend,
	parseISO,
	startOfToday,
	startOfWeek,
	startOfMonth,
	endOfMonth,
} from 'date-fns';
import moment, {type Moment} from 'moment';

import CompanySetupUtil from './CompanySetupUtil';
import {NullablePartial} from './type-util/NullablePartial';

export type DateRange = {
	startDate: Date;
	endDate: Date;
};

export type DateRangeYMD = {
	startYear: number;
	startMonth: number;
	startDay: number;
	endYear: number;
	endMonth: number;
	endDay: number;
};

/**
 * We want to migrate away from Moment.js, so dates will start transitioning
 * to standard js dates using date-fns.
 * Keep all date functions accepting Moment arguments compatible with moment.js as well as standard js dates
 * use isMoment to check if a date is a moment date, if not use date-fns
 * */
export function isMoment(dateObject: Moment | Date | null): boolean {
	return !!dateObject && moment.isMoment(dateObject);
}

export function isDateDisabled(dateObject: Moment | Date | null): boolean {
	if (dateObject && CompanySetupUtil.weekendsAlwaysHidden()) {
		if (isMoment(dateObject)) {
			const momentDate = dateObject as Moment;
			// Is sunday or saturday
			return [0, 6].includes(momentDate.day());
		} else {
			return isWeekend(dateObject as Date);
		}
	}

	return false;
}

type DateFragmentPropsNullable = {
	year: number | null;
	month: number | null;
	day: number | null;
};

export function CreateMomentDate(year: number | null, month: number | null, day: number | null): Moment | null {
	if (!!year && !!month && !!day) {
		return moment.utc({y: year, M: month - 1, d: day});
	}
	return null;
}

export function isDateDisabledYMD(year: number | null, month: number | null, day: number | null): boolean {
	return isDateDisabled(CreateMomentDate(year, month, day));
}

export function dateFragmentPropsFromIsoDateString(isoDateString: string | null): DateFragmentPropsNullable {
	if (!isoDateString) {
		return {
			year: null,
			month: null,
			day: null,
		};
	}
	const date: Date = parseISO(isoDateString);
	return {
		year: getYear(date),
		month: getMonth(date) + 1,
		day: getDate(date),
	};
}

type CreateDateInput = {
	year: number;
	month: number;
	day: number;
};
export function createDateFromYMD({year, month, day}: CreateDateInput): Date {
	return new Date(year, month - 1, day);
}

export function createDateFromNullableYMD({year, month, day}: NullablePartial<CreateDateInput>): Date | null {
	if (year && month && day) {
		return createDateFromYMD({year, month, day});
	}

	return null;
}

export function createDateFromMoment(moment: Moment | Date): Date {
	if (moment instanceof Date) {
		return moment;
	}
	return createDateFromYMD({
		year: moment.year(),
		month: moment.month() + 1,
		day: moment.date(),
	});
}

export function isYearMonthDayBeforeToday(year: number | null, month: number | null, day: number | null): boolean {
	if (!!year && !!month && !!day) {
		const date: Date = createDateFromYMD({
			year,
			month,
			day,
		});
		if (date) {
			return isBefore(date, startOfToday());
		}
	}
	return false;
}

export function getWeekDateRangeFromDate(date: Date): DateRange {
	const startDate = startOfWeek(date);
	const endDate = endOfWeek(date);

	return {startDate, endDate};
}

export function dateRangeToYMD(dateRange: DateRange): DateRangeYMD {
	return {
		startYear: getYear(dateRange.startDate),
		startMonth: getMonth(dateRange.startDate) + 1,
		startDay: getDate(dateRange.startDate),
		endYear: getYear(dateRange.endDate),
		endMonth: getMonth(dateRange.endDate) + 1,
		endDay: getDate(dateRange.endDate),
	};
}

export function dateFragmentPropsFromIntervalInIsoFormat(startDateIsoString: string, endDateIsoString: string): DateRangeYMD {
	const startDate = parseISO(startDateIsoString);
	const endDate = parseISO(endDateIsoString);

	return dateRangeToYMD({startDate, endDate});
}

export function isoDateToMomentNonUtc(isoDate: string): Moment | null {
	if (isoDate) return moment(isoDate);
	return null;
}
export function dateObjectToIsoDate(dateObject: Moment | Date | null | undefined): string | null {
	if (!dateObject) {
		return null;
	}
	if (isMoment(dateObject)) {
		const momentDate = dateObject as Moment;
		return momentDate.format('YYYY-MM-DD');
	} else {
		return formatISO(dateObject as Date, {representation: 'date'});
	}
}

export function getMonthRange(dateObject: Date): DateRange {
	const startDate = startOfMonth(dateObject);
	const endDate = endOfMonth(dateObject);

	return {startDate, endDate};
}

export function getPreviousMonthRange(dateObject: Date): DateRange {
	const date = new Date(dateObject);

	date.setMonth(date.getMonth() - 1);

	return getMonthRange(date);
}

export function getNextMonthRange(dateObject: Date): DateRange {
	const date = new Date(dateObject);

	date.setMonth(date.getMonth() + 1);

	return getMonthRange(date);
}
